import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from './api';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrated: false,
      
      setHydrated: (value) => set({ isHydrated: value }),
      
      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/auth/login', { email, password });
          const { token, user } = response.data;
          localStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Login failed' };
        }
      },
      
      register: async (username, email, password) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/auth/register', { username, email, password });
          const { token, user } = response.data;
          localStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Registration failed' };
        }
      },
      
      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false });
      },
      
      fetchUser: async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        try {
          const response = await api.get('/auth/me');
          set({ user: response.data, isAuthenticated: true, token });
        } catch (error) {
          localStorage.removeItem('token');
          set({ user: null, token: null, isAuthenticated: false });
        }
      },
      
      updateBalance: (balance) => {
        const user = get().user;
        if (user) {
          set({ user: { ...user, balance } });
        }
      }
    }),
    {
      name: 'crackbox-auth',
      partialize: (state) => ({ 
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

export const useCartStore = create((set, get) => ({
  items: [],
  total: 0,
  isLoading: false,
  
  fetchCart: async () => {
    set({ isLoading: true });
    try {
      const response = await api.get('/cart');
      set({ items: response.data.items, total: response.data.total, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
    }
  },
  
  addToCart: async (productId, quantity = 1) => {
    try {
      await api.post('/cart/add', { product_id: productId, quantity });
      await get().fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to add to cart' };
    }
  },
  
  removeFromCart: async (productId) => {
    try {
      await api.delete(`/cart/${productId}`);
      await get().fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to remove from cart' };
    }
  },
  
  updateQuantity: async (productId, quantity) => {
    try {
      await api.put(`/cart/${productId}?quantity=${quantity}`);
      await get().fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update cart' };
    }
  },
  
  checkout: async () => {
    try {
      const response = await api.post('/checkout');
      set({ items: [], total: 0 });
      return { success: true, orderId: response.data.order_id };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Checkout failed' };
    }
  },
  
  clearCart: () => set({ items: [], total: 0 })
}));

export const useNotificationStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  
  fetchNotifications: async () => {
    try {
      const response = await api.get('/notifications');
      const notifications = response.data;
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  },
  
  markAsRead: async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      const notifications = get().notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }
}));

export const useChatStore = create((set, get) => ({
  messages: [],
  isOpen: false,
  isLoading: false,
  
  toggleChat: () => set({ isOpen: !get().isOpen }),
  openChat: () => set({ isOpen: true }),
  closeChat: () => set({ isOpen: false }),
  
  fetchMessages: async () => {
    set({ isLoading: true });
    try {
      const response = await api.get('/chat/messages');
      set({ messages: response.data, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
    }
  },
  
  sendMessage: async (content) => {
    try {
      await api.post('/chat/messages', { content });
      await get().fetchMessages();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to send message' };
    }
  }
}));
